"use client";

import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from "react";

export type GlassIconButtonKind = "neutral" | "accent" | "destructive";

export interface GlassIconButtonProps
	extends ButtonHTMLAttributes<HTMLButtonElement> {
	readonly label: string;
	readonly kind?: GlassIconButtonKind;
	/** Optional on-media foreground (white) for controls over artwork. */
	readonly onMedia?: boolean;
	readonly children: ReactNode;
}

function kindStyle(
	kind: GlassIconButtonKind,
	onMedia: boolean,
): CSSProperties | undefined {
	if (onMedia) {
		return {
			color: "var(--ds-on-media-primary)",
			background: "rgb(0 0 0 / 0.42)",
			borderColor:
				"color-mix(in srgb, var(--ds-on-media-primary) 30%, transparent)",
		};
	}
	switch (kind) {
		case "accent":
			return {
				color: "var(--ds-background)",
				background: "var(--ds-accent)",
				borderColor: "var(--ds-accent)",
			};
		case "destructive":
			return { color: "var(--ds-destructive)" };
		default:
			return undefined;
	}
}

/**
 * GlassIconButton — 44×44 circular icon button (neutral / accent / destructive).
 * Mirrors `DSGlassIconButton` on iOS, including the on-media variant.
 */
export function GlassIconButton({
	label,
	kind = "neutral",
	onMedia = false,
	children,
	className = "",
	style,
	...props
}: Readonly<GlassIconButtonProps>) {
	return (
		<button
			className={`ds-icon-button ${className}`.trim()}
			aria-label={label}
			style={{ ...kindStyle(kind, onMedia), ...style }}
			{...props}
		>
			{children}
		</button>
	);
}
